import {
  SET_CERT_DATA,
  SET_CERT_LOADING,
  SET_CERT_FILTERS,
  NETWORK_REQUEST_SENT,
} from './types';
import { BACKEND_URL_PREFIX } from '../utils/const';
import { buildDefaultHeaders } from './helper';
import { checkAndUpdateToken } from './auth';
import { createNotification } from '../components/notifications/notifications';

export const setCertFilters = (payload) => ({ type: SET_CERT_FILTERS, payload });
export const setCertData = (payload) => ({ type: SET_CERT_DATA, payload });

const buildQuery = (params) => {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([key, val]) => {
    if (val != null && val !== '') sp.set(key, val);
  });
  return sp.toString();
};

// Текст бизнес-ошибки из ответа бэка ({ error }) либо запасной текст.
const responseError = async (response, fallback) => {
  try {
    const result = await response.json();
    return result.error || fallback;
  } catch (e) {
    return fallback;
  }
};

/* ---------- Фильтры ---------- */

// Учебные годы. GET /cert/years
export const getCertYears = () => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (getState().auth.token) {
    const response = await fetch(
      `${BACKEND_URL_PREFIX}/cert/years`,
      buildDefaultHeaders(getState)
    );

    dispatch({ type: NETWORK_REQUEST_SENT, response });

    if (response.ok) {
      const result = await response.json();
      dispatch(setCertFilters({ years: result.years }));
      return result.years;
    }
  }
};

// Учебные периоды выбранного года. GET /cert/terms?year_id=
export const getCertTerms = (yearId) => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (getState().auth.token) {
    const response = await fetch(
      `${BACKEND_URL_PREFIX}/cert/terms?year_id=${yearId}`,
      buildDefaultHeaders(getState)
    );

    dispatch({ type: NETWORK_REQUEST_SENT, response });

    if (response.ok) {
      const result = await response.json();
      dispatch(setCertFilters({ terms: result.terms }));
      return result.terms;
    }
  }
};

// Факультеты (значение «Все» добавляет фронт). GET /cert/faculties
export const getCertFaculties = () => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (getState().auth.token) {
    const response = await fetch(
      `${BACKEND_URL_PREFIX}/cert/faculties`,
      buildDefaultHeaders(getState)
    );

    dispatch({ type: NETWORK_REQUEST_SENT, response });

    if (response.ok) {
      const result = await response.json();
      dispatch(setCertFilters({ faculties: result.faculties }));
      return result.faculties;
    }
  }
};

// Виды занятий (фильтр «Тип») выбранного вида аттестации. GET /cert/exam_types
export const getCertExamTypes = (examKind) => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (getState().auth.token) {
    const response = await fetch(
      `${BACKEND_URL_PREFIX}/cert/exam_types?exam_kind=${examKind}`,
      buildDefaultHeaders(getState)
    );

    dispatch({ type: NETWORK_REQUEST_SENT, response });

    if (response.ok) {
      const result = await response.json();
      dispatch(setCertFilters({ examTypes: result.exam_types }));
      return result.exam_types;
    }
  }
};

// Группы, имеющие ведомости по фильтрам. GET /cert/groups
// params: { exam_kind, year_id, trmid, faculty_id, typeids }
export const getCertGroups = (params) => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (getState().auth.token) {
    const response = await fetch(
      `${BACKEND_URL_PREFIX}/cert/groups?${buildQuery(params)}`,
      buildDefaultHeaders(getState)
    );

    dispatch({ type: NETWORK_REQUEST_SENT, response });

    if (response.ok) {
      const result = await response.json();
      dispatch(setCertFilters({ groups: result.groups }));
      return result.groups;
    }
  }
};

// Дисциплины группы по фильтрам. GET /cert/disciplines
// params: { exam_kind, gid, trmid, typeids }
export const getCertDisciplines = (params) => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (getState().auth.token) {
    const response = await fetch(
      `${BACKEND_URL_PREFIX}/cert/disciplines?${buildQuery(params)}`,
      buildDefaultHeaders(getState)
    );

    dispatch({ type: NETWORK_REQUEST_SENT, response });

    if (response.ok) {
      const result = await response.json();
      dispatch(setCertFilters({ disciplines: result.disciplines }));
      return result.disciplines;
    }
  }
};

// Справочник оценок schedule_grades_values. GET /journal/grade_values
export const getCertGradeValues = () => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (getState().auth.token) {
    const response = await fetch(
      `${BACKEND_URL_PREFIX}/journal/grade_values`,
      buildDefaultHeaders(getState)
    );

    dispatch({ type: NETWORK_REQUEST_SENT, response });

    if (response.ok) {
      const result = await response.json();
      dispatch(setCertFilters({ gradeValues: result.grade_values }));
      return result.grade_values;
    }
  }
};

/* ---------- Ведомость ---------- */

// Данные ведомости (колонки/обучающиеся/оценки/итоги). GET /cert/grid
// params: { exam_kind, gid, trmid, cid, typeids }
// quiet — перезапрос без глобального лоадера (после сохранения оценки/закрытия).
export const getCertGrid = (params, quiet = false) => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (getState().auth.token) {
    if (!quiet) dispatch({ type: SET_CERT_LOADING, payload: true });

    const response = await fetch(
      `${BACKEND_URL_PREFIX}/cert/grid?${buildQuery(params)}`,
      buildDefaultHeaders(getState)
    );

    dispatch({ type: NETWORK_REQUEST_SENT, response });

    if (response.ok) {
      const result = await response.json();
      dispatch({ type: SET_CERT_DATA, payload: result });
    } else if (!quiet) {
      dispatch({ type: SET_CERT_DATA, payload: null });
      createNotification(
        'error',
        'Аттестация',
        await responseError(response, 'Не удалось загрузить ведомость')
      );
    }

    if (!quiet) dispatch({ type: SET_CERT_LOADING, payload: false });
  }
};

// Сохранить/очистить оценку. POST /cert/grade
// body: { exam_kind, gid, cid, sheid, week_id, trmid, trmtid, student_mid,
//         grade, jcid, retake, teacher_mid, comment }
export const saveCertGrade = (body) => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (getState().auth.token) {
    const response = await fetch(`${BACKEND_URL_PREFIX}/cert/grade`, {
      ...buildDefaultHeaders(getState),
      method: 'post',
      body: JSON.stringify(body),
    });

    dispatch({ type: NETWORK_REQUEST_SENT, response });

    if (response.ok) {
      return response.json();
    }

    createNotification(
      'error',
      'Аттестация',
      await responseError(response, 'Не удалось сохранить оценку')
    );
  }
};

// Закрыть ведомость. POST /cert/close, body: { exam_kind, jcid, force }
// Возвращает { status: 'success' } либо { status: 'warning',
// requires_confirmation, message } — подтверждение при пустых оценках.
export const closeCertSheet = (body) => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (getState().auth.token) {
    const response = await fetch(`${BACKEND_URL_PREFIX}/cert/close`, {
      ...buildDefaultHeaders(getState),
      method: 'post',
      body: JSON.stringify(body),
    });

    dispatch({ type: NETWORK_REQUEST_SENT, response });

    if (response.ok) {
      return response.json();
    }

    createNotification(
      'error',
      'Аттестация',
      await responseError(response, 'Не удалось закрыть ведомость')
    );
  }
};
